import React from 'react';
import {
  getVideoOrientation,
  getVideoAspectRatio,
  isPortraitUrl,
  isSquareUrl
} from '../shared/videoUtils';

export { getVideoOrientation, getVideoAspectRatio, isPortraitUrl, isSquareUrl };

/**
 * Extracts 11-character YouTube Video ID from any valid YouTube URL format
 * (watch?v=, /live/, /shorts/, /embed/, /v/, or youtu.be/)
 */
export function getYouTubeId(url) {
  if (!url) return null;
  const str = String(url).trim();
  const match = str.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|live\/|shorts\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
  return match ? match[1] : null;
}

/**
 * Checks if a URL is a Facebook video link
 */
export function isFacebookUrl(url) {
  if (!url) return false;
  const str = String(url).trim();
  return str.includes('facebook.com') || str.includes('fb.watch');
}

/**
 * Backward compatible helper for checking vertical portrait videos
 */
export function isPortraitVideo(url) {
  return isPortraitUrl(url);
}

/**
 * Strips tracking query parameters and converts Share links to canonical format
 */
export function sanitizeFacebookUrl(url) {
  if (!url) return '';
  let str = String(url).trim();
  str = str.replace(/[?&](?:fbclid|__tn__|__cft__|_rdr)=[^&]*/g, '');
  str = str.replace(/[?&]$/, '');

  const reelMatch = str.match(/(?:facebook\.com\/(?:reel|reels)\/|fb\.watch\/)([a-zA-Z0-9_-]+)/);
  if (reelMatch) {
    return `https://www.facebook.com/reel/${reelMatch[1]}`;
  }

  const watchMatch = str.match(/facebook\.com\/watch\/\?v=(\d+)/);
  if (watchMatch) {
    return `https://www.facebook.com/watch/?v=${watchMatch[1]}`;
  }

  return str;
}

/**
 * Ultra-crisp, premium VideoPlayer component with automatic frame sizing.
 * Automatically fits 16:9 widescreen, 9:16 vertical reels, 1:1 square media,
 * and exact video dimension ratios without letterboxing gaps.
 */
export default function VideoPlayer({ item, url, title = 'Video Player', className = '' }) {
  const target = item || url;

  if (!target) return null;

  const linkStr = typeof target === 'string'
    ? target.trim()
    : String(target.videoLink || target.link || target.video_url || target.fb_url || target.url || '').trim();

  if (!linkStr) return null;

  // 1. YouTube Video / Live Stream / Shorts
  const ytId = getYouTubeId(linkStr);
  if (ytId) {
    const embedUrl = `https://www.youtube.com/embed/${ytId}?autoplay=1&mute=0&enablejsapi=1&rel=0`;

    return (
      <div className={`w-full h-full relative bg-black flex items-center justify-center overflow-hidden ${className}`}>
        <iframe
          title={title}
          src={embedUrl}
          className="w-full h-full border-0 absolute inset-0 block"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen={true}
        />
      </div>
    );
  }

  // 2. Facebook Video / FB Watch / FB Reel / FB Share
  if (isFacebookUrl(linkStr)) {
    const cleanFbUrl = sanitizeFacebookUrl(linkStr);
    // Setting autoplay=false prevents Facebook's player engine from forcing muted=true (🔊x)
    const fbEmbedUrl = `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(cleanFbUrl)}&show_text=false&allowfullscreen=true&autoplay=false`;

    return (
      <div className={`w-full h-full relative bg-black flex items-center justify-center overflow-hidden group ${className}`}>
        <iframe
          title={title}
          src={fbEmbedUrl}
          className="w-full h-full border-0 absolute inset-0 block"
          style={{ border: 'none', overflow: 'hidden', width: '100%', height: '100%' }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen={true}
        />
      </div>
    );
  }

  // 3. Direct HTML5 Video File / Generic Stream
  return (
    <div className={`w-full h-full flex items-center justify-center bg-black overflow-hidden relative ${className}`}>
      <video
        src={linkStr}
        controls
        autoPlay={true}
        muted={false}
        onLoadedMetadata={(e) => {
          if (e.target) {
            e.target.muted = false;
            e.target.volume = 1.0;
            e.target.play().catch(() => { });
          }
        }}
        onCanPlay={(e) => {
          if (e.target) {
            e.target.muted = false;
            e.target.volume = 1.0;
            e.target.play().catch(() => { });
          }
        }}
        className="w-full h-full object-contain absolute inset-0"
      />
    </div>
  );
}
